import { LitElement, html, css, PropertyValues, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { LegData } from '../../../src/models/vessel-performance-model';
import { PerformanceUtils } from '../../../src/utils/performance-utils';

@customElement('dial-performance-chart')
export class DialPerformanceChart extends LitElement {
    @property({type: Object}) data: LegData = PerformanceUtils.ZeroLegData();
    @property({attribute: false}) private _rangeAngle: number = 0;

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: row;
                font-family: 'MaerskText';
            }

            .header {
                display: flex;
                flex-direction: column;
                writing-mode: vertical-rl;
                transform: rotate(180deg);
                justify-content: space-around;
                align-items: center;
                background: var(--neutrals-grey-10);
                min-width: 36px;
            }

            .title {
                color: var(--neutrals-grey-03, black);
                font-size: 11px;
            }

            .graph {
                background: var(--performance-viewer-graph-background);
                width: 400px;
                height: 134px;
                position: relative;
            }

            .data, .persantage .text{
                color: white;
                font-family: 'MaerskText'; 
                font-size: 11px;
            }

            .wrapper {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                margin-top: 24px;
                position: absolute;
                top: 0;
                right: 22px;
                width: 144px;
            }

            .actual {
                color: var(--performance-viewer-actual-color);
            }

            .simulated {
                color: var(--performance-viewer-simulated-color);
            }

            .nextleg {
                color: var(--performance-viewer-nextleg-color);
            }
        `];
    }
    
    render() {
        return html`
            <div class="header">
              <span class="title">${this.data.title} (${this.data.unit})</span>
            </div>
            <div class="graph"> 
                ${this.data.simulated?.lower !== undefined && this.data.simulated?.upper !== undefined ? html`
                <svg
                    class="canvas"
                    width="315"
                    height="134">
                    <g transform="translate(0 82) rotate(180)" transform-origin="center">
                        <circle
                            class="background"
                            stroke-width="34"
                            fill="transparent"
                            stroke="var(--performance-viewer-dial-background)"
                            r="78.5"
                            cx="191"
                            cy="100"
                            />
                        <circle
                            class="simulated"
                            stroke-width="17"
                            fill="transparent"
                            stroke="var(--performance-viewer-simulated-color)"
                            r="70"
                            cx="191"
                            cy="100"
                            />
                        <circle
                            class="actual"
                            stroke-width="17"
                            fill="transparent"
                            stroke="var(--performance-viewer-actual-color)"
                            r="87"
                            cx="191"
                            cy="100"/>
                    </g>
                    <g transform-origin="124px 116px" transform="rotate(${this._rangeAngle})" >
                        <circle
                            class="range"
                            stroke-width="3.5"
                            fill="transparent"
                            stroke="var(--performance-viewer-range-color)"
                            r="70"
                            cx="124"
                            cy="116"/>
                    </g>
                    <g transform-origin="110.5 0" transform="translate(13.5 116) rotate(${(this.data.nextLeg || 0) / (this.data.max ?? 1)! * 180})">
                        <line class="nextleg" x1="0" y1="0" x2="32" y2="0" stroke-width="3" stroke="var(--performance-viewer-nextleg-color)"/>
                    </g>
                    <g transform-origin="75.6 0" transform="translate(48.4 116) rotate(${this.data.simulated.lower / (this.data.max ?? 1)! * 180})">
                        <line class="nextleg" x1="0" y1="0" x2="11.2" y2="0" stroke-width="3" stroke="var(--performance-viewer-range-color)"/>
                    </g>
                    <g transform-origin="75.6 0" transform="translate(48.4 116) rotate(${this.data.simulated.upper / (this.data.max ?? 1)! * 180})">
                        <line class="nextleg" x1="0" y1="0" x2="11.2" y2="0" stroke-width="3" stroke="var(--performance-viewer-range-color)"/>
                    </g>
                    <g>
                        <text x="45.5" y="130" style="font-family: 'MaerskText'; font-size: 12px;" text-anchor="middle" fill="var(--performance-viewer-dial-text-color)">
                            0
                        </text>
                        <text x="202.5" y="130" style="font-family: 'MaerskText'; font-size: 12px;" text-anchor="middle" fill="var(--performance-viewer-dial-text-color)">
                            ${(this.data.max ?? 1)!.toFixed(0)}
                        </text>
                    </g>
                </svg>` : html`
                <svg
                    class="canvas"
                    width="315"
                    height="134">
                    <g transform="translate(0 82) rotate(180)" transform-origin="center">
                        <circle
                            class="background"
                            stroke-width="34"
                            fill="transparent"
                            stroke="var(--performance-viewer-dial-background)"
                            r="78.5"
                            cx="191"
                            cy="100"
                            />
                        <circle
                            class="simulated"
                            stroke-width="17"
                            fill="transparent"
                            stroke="var(--performance-viewer-simulated-color)"
                            r="70"
                            cx="191"
                            cy="100"
                            />
                        <circle
                            class="actual"
                            stroke-width="17"
                            fill="transparent"
                            stroke="var(--performance-viewer-actual-color)"
                            r="87"
                            cx="191"
                            cy="100"/>
                    </g>
                    <g transform-origin="110.5 0" transform="translate(13.5 116) rotate(${(this.data.nextLeg || 0) / (this.data.max ?? 1)! * 180})">
                        <line class="nextleg" x1="0" y1="0" x2="32" y2="0" stroke-width="3" stroke="var(--performance-viewer-nextleg-color)"/>
                    </g>
                    <g>
                        <text x="45.5" y="130" style="font-family: 'MaerskText'; font-size: 12px;" text-anchor="middle" fill="var(--performance-viewer-dial-text-color)">
                            0
                        </text>
                        <text x="202.5" y="130" style="font-family: 'MaerskText'; font-size: 12px;" text-anchor="middle" fill="var(--performance-viewer-dial-text-color)">
                            ${(this.data.max ?? 1)!.toFixed(0)}
                        </text>
                    </g>
                </svg>`}
                ${this._metricsContainer(this.data.simulated?.lower !== undefined && this.data.simulated?.upper !== undefined)}
            </div>
        `;
    }

    private _setSimulated(percent: number) {
        this._setProgress(percent, '.simulated');
    }

    private _setActual(percent: number) {
        this._setProgress(percent, '.actual');
    }

    private _setRange(percent: number) {
        this._setProgress(percent, '.range');
    }

    private _setProgress(percent: number, component: string) {
        let circle = <SVGCircleElement>this.shadowRoot!.querySelector(component);
        let radius = circle.r.baseVal.value;
        let circumference = radius * 2 * Math.PI;

        circle.style.strokeDasharray = `${circumference} ${circumference}`;
        circle.style.strokeDashoffset = `${circumference}`;

        const offset = circumference - percent / 200 * circumference;
        circle.style.strokeDashoffset = `${offset}`;
    }

    updated(changedProperties: PropertyValues) {
        this._setSimulated(((this.data.simulated?.value || 0) / this.data.max!)*100);
        this._setActual(((this.data.current || 0) / this.data.max!)*100);
        
        if (this.data.simulated?.lower !== undefined && this.data.simulated?.upper !== undefined) {
            this._setRange((this.data.simulated.upper - this.data.simulated.lower) / this.data.max! * 100);
            this._rangeAngle = this.data.simulated.lower / this.data.max! * 180 + 180;
        }
    }

    firstUpdated() {
        this._setProgress(100, '.background');
    }

    private _metricsContainer(simulated_upper_lower_values: boolean) {
        return html`
            <div class="wrapper">
                <div class="data">
                    ${this.data.current ? html`<div>
                        <span class="actual">-</span>
                        <span class="text">${this.data.current === null ? "N/A" : this.data.current.toFixed(0)}</span>
                    </div>` : nothing}
                    <div>
                        <span class="simulated">-</span>
                        <span class="text">${this.data.simulated === null ? "N/A" : this.data.simulated.value.toFixed(0)}</span>
                    </div>
                    ${!simulated_upper_lower_values ? nothing : html`
                        <div>
                            <span class="simulated">-</span>
                            <span class="text">${`${this.data.simulated?.lower?.toFixed(0)} - ${this.data.simulated?.upper?.toFixed(0)}`}</span>
                        </div>
                    ` }
                    <div>
                        <span class="next_leg">-</span>
                        <span class="text">${this.data.nextLeg === null ? "N/A" : this.data.nextLeg.toFixed(0)}</span>
                    </div>
                </div>
                ${this.data.title === "ME POWER" ? html`
                    <div class="persantage">
                        <div class="text">${this._convertToPercent(this.data.max, this.data.current)}</div>
                        <div class="text">${this._convertToPercent(this.data.max, this.data?.simulated?.value)}</div>
                        ${!simulated_upper_lower_values ? nothing : html`
                            <div class="text">${this._convertToPercent(this.data.max, this.data?.simulated?.lower)} - ${this._convertToPercent(this.data.max, this.data?.simulated?.upper)}</div>
                        `}
                        <div class="text">${this._convertToPercent(this.data.max,this.data.nextLeg)}</div>
                    </div>
                ` : nothing}
            </div>
        `;
    }


    private _convertToPercent(maximumPower?: number, value?: number | null) {
        if (!maximumPower || !value || value == 0) {
            return "N/A"
        }

        return `${((value / maximumPower) * 100).toFixed(0)} %`;
    }
};
